import { useCallback } from 'react';
import { createResource } from '@atlassian/react-resource-router';
import type { PreferenceOptions, UserPreferences } from './types.tsx';
import {
	DEFAULT_EDITOR_PREFERENCE_KEY,
	WORKFLOW_USER_PREFERENCES_KEY,
	CHANGEBOARDING_DISMISSED_PREFERENCE_KEY,
	COLLAPSE_TRANSITION_LABELS_PREFERENCE_KEY,
} from './constants.tsx';

import { getUserPreferences } from './services/get-user-preferences/index.tsx';
import { useUserPreference, useUserPreferencesInitialiser } from './hooks/index.tsx';

const workflowUserPreferenceKeys = [
	DEFAULT_EDITOR_PREFERENCE_KEY,
	CHANGEBOARDING_DISMISSED_PREFERENCE_KEY,
	COLLAPSE_TRANSITION_LABELS_PREFERENCE_KEY,
] as const;

export const workflowUserPreferencesResource = createResource<UserPreferences>({
	type: WORKFLOW_USER_PREFERENCES_KEY,
	getKey: () => WORKFLOW_USER_PREFERENCES_KEY,
	getData: () => getUserPreferences([...workflowUserPreferenceKeys]),
	maxAge: 1000,
	isBrowserOnly: true,
});

type WorkflowUserPreferenceKey = (typeof workflowUserPreferenceKeys)[number];

export const useWorkflowUserPreference = (
	key: WorkflowUserPreferenceKey,
	options?: PreferenceOptions,
) => useUserPreference(workflowUserPreferencesResource, key, options);

export const useWorkflowUserPreferencesInitialiser = () =>
	useUserPreferencesInitialiser(workflowUserPreferencesResource);

export const useCollapseTransitionLabelsPreferenceFromResource = () => {
	const { value, setValue } = useWorkflowUserPreference(COLLAPSE_TRANSITION_LABELS_PREFERENCE_KEY);

	const areTransitionLabelsCollapsed = !value ? true : value === 'true';

	const setTransitionLabelsCollapsed = useCallback(
		(collapsed: boolean) => {
			setValue(collapsed.toString());
		},
		[setValue],
	);

	return { value: areTransitionLabelsCollapsed, setValue: setTransitionLabelsCollapsed };
};
